import {
  ActivePeriodsSchema,
  Booking,
  BookingSchema,
  BookingSettingsSchema,
  BundleLineId,
  BundleLineVariantId,
  BundleSchema,
  FloorPlanSchema,
  GreeterEventSchema,
  Quantity,
  SpecialOpeningHoursSchema,
  SpecialPricePeriodsSchema,
  TableId,
  TableIdSchema,
  TicketEvent,
  TicketEventSchema,
  Venue,
  VenueSchema,
} from "@greeter/core";
import {
  ActorRefFrom,
  assign,
  emit,
  enqueueActions,
  raise,
  setup,
} from "xstate";
import {
  adapt,
  notImplemented,
  PersistedSnapshotSchema,
} from "@greeter/xstate-util";
import { change, ChangeStateSchema, createChangeState } from "@greeter/changes";
import { $ZodErrorTree } from "zod/v4/core";
import * as z from "zod";
import { NotImplementedError } from "@greeter/error";
import {
  ApiError,
  CreateBookingV2Request,
  IGuestApi,
  OrderLineRequest,
} from "@greeter/api";
import { assert } from "@greeter/assert";
import {
  CartItemBundle,
  CartItemBundleForm,
  CartMap,
  CartMapSchema,
} from "./Cart/CartMachine";
import Routes from "@greeter-guest/utility/Routes";
import { DatePeriod, isWithin, TimeOfDay, withTimeOfDay } from "@greeter/date";
import { endOfMonth, startOfMonth, subDays } from "date-fns";
import { JsonMapSchema } from "@greeter/json";
import { formatMoney } from "@greeter/formatters";
import { Log } from "@greeter/log";
import { indexBundleLineVariants } from "./BundlePicker/BundlePickerV3/indexing";
import { pushTo } from "@greeter/array";
import { v7 } from "uuid";
import { isType, getServiceDayPeriod } from "@greeter/util";

export const PracticalInfoFormSchema = z
  .object({
    groupSize: z.number().min(1, "Skal min. være 1").default(1),
    venueId: z.string(),
    comment: z
      .string()
      .min(0)
      .max(500, "Kan maks være 500 karakterer langt")
      .optional(),
    tables: z
      .array(
        z.object({
          area: z.string(),
          tableNumber: z.number(),
          minPrice: z.number(),
        }),
      )
      .min(1, "Du skal have valgt min. 1 bord."),
    total: z.number().min(0).default(0),
    minPrice: z.number().min(0).default(0),
    arrivalTime: z
      .union([z.undefined(), z.string(), z.date()])
      .refine((a) => a !== undefined, "Du skal vælge et tidspunkt")
      // @ts-ignore
      .transform((s) => new Date(s))
      .refine((a) => new Date() < a, "Du skal vælge et tidspunkt i fremtiden"),
    arrivalDate: z
      .union([z.undefined(), z.string(), z.date()])
      .refine((a) => a !== undefined, "Du skal vælge en dato")
      // @ts-ignore
      .transform((s) => new Date(s))
      .refine((a) => a >= withTimeOfDay(new Date(), TimeOfDay.parse("00:00")), {
        error: "Du skal vælge et tidspunkt i fremtiden",
      }),
  })
  .refine(
    (form) => form.total && form.minPrice >= 0 && form.total >= form.minPrice,
    {
      abort: true,
      error(form) {
        return `Min. pris for de borde du har valgt er ${formatMoney(
          (form.input as any).minPrice ?? 0,
        )} kr. Du skal putte mere i kurven eller vælge et andet bord`;
      },
      path: ["total"],
    },
  );
export type PracticalInfoForm = z.infer<typeof PracticalInfoFormSchema>;

const QuantitySchema = z.number().min(1);
const CartLineSchema = z.object({
  bundleId: z.string(),
  bundleLines: JsonMapSchema(
    z.string(),
    z.object({
      bundleLineId: z.string(),
      bundleLineVariants: z.map(z.string(), QuantitySchema),
    }),
  ),
});
const CartFormSchema = z.object({
  lines: z.array(z.object({})),
});
type CartForm = z.infer<typeof CartFormSchema>;

const BookingFormSchema = z.intersection(
  PracticalInfoFormSchema,
  CartFormSchema,
);

type BookingForm = z.infer<typeof BookingFormSchema>;

/**
 * NOTE: We use zod here to make it easy to serialize and deserialize
 */
const PracticalInfoChangeSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("setComment"), data: z.string() }),
  z.object({
    type: z.literal("setArrivalTime"),
    data: z.string().transform((s) => new Date(s)),
  }),
  z.object({
    type: z.literal("setArrivalDate"),
    data: z.string().transform((s) => new Date(s)),
  }),
  z.object({
    type: z.literal("selectTable"),
    data: z.object({
      area: z.string(),
      tableNumber: z.number(),
      minPrice: z.number(),
    }),
  }),
  z.object({ type: z.literal("setGroupSize"), data: z.number() }),
  z.object({ type: z.literal("setMinPrice"), data: z.number() }),
  z.object({ type: z.literal("setTotal"), data: z.number() }),
]);

export type PracticalInfoChange = z.infer<typeof PracticalInfoChangeSchema>;

function applyPracticalInfoChange(
  form: Partial<PracticalInfoForm>,
  change: PracticalInfoChange,
) {
  //const logTag = `[${applyPracticalInfoChange.name}][${change.type}]`;
  switch (change.type) {
    case "setGroupSize":
      form.groupSize = change.data;
      break;
    case "setArrivalDate":
      form.arrivalDate = change.data;
      form.arrivalTime = undefined;
      form.minPrice = 0;
      form.tables = [];
      break;
    case "setArrivalTime":
      form.arrivalTime = change.data;
      form.minPrice = 0;
      form.tables = [];
      break;
    case "setTotal":
      form.total = change.data;
      break;
    case "setMinPrice":
      form.minPrice = change.data;
      break;
    case "setComment":
      form.comment = change.data;
      break;
    case "selectTable":
      form.tables ??= [];

      const idx = form.tables.findIndex(
        (t) =>
          t.tableNumber === change.data.tableNumber &&
          t.area === change.data.area,
      );

      if (idx > -1) {
        form.tables.splice(idx, 1);
        break;
      }

      form.tables.push(change.data);

      let minPrice = 0;
      for (let t of form.tables) {
        if (typeof t.minPrice !== "number") continue;
        minPrice += t.minPrice;
      }
      form.minPrice = minPrice;
      break;
  }
}

type Picks = Record<BundleLineId, Record<BundleLineVariantId, Quantity>>;
function mapCartItemToRecord(cartItemBundle: CartItemBundle): Picks {
  const picks: Picks = {};
  for (const [
    bundleLineKey,
    bundleLinePicks,
  ] of cartItemBundle.picks.entries()) {
    const variantPicks = {};

    for (const [variantKey, variantPick] of bundleLinePicks.entries()) {
      let quantity = 0;
      for (const variantPickData of variantPick.values()) {
        if (variantPickData.type === "normal") {
          quantity += variantPickData.quantity;
        }
      }
      variantPicks[variantKey] = quantity;
    }

    picks[bundleLineKey] = variantPicks;
  }
  return picks;
}

function mapBundleFormToPicks(cartItemBundleForm: CartItemBundleForm): Picks {
  const picks: Picks = {};
  for (const line of cartItemBundleForm.form.lines) {
    const variantPicks = {};

    for (const variant of line.variants.filter(
      (v) => v.quantity && v.quantity > 0,
    )) {
      variantPicks[variant.id] = variant.quantity;
    }

    picks[line.id] = variantPicks;
  }
  return picks;
}

function mapBundleFormExtrasToSimpleOrderLineRequests(
  cartItemBundleForm: CartItemBundleForm,
): OrderLineRequest[] {
  let variants = indexBundleLineVariants([cartItemBundleForm.form]);
  let result: OrderLineRequest[] = [];
  for (const upsell of cartItemBundleForm.form.lines
    .map((l) => l.extra)
    .flat()) {
    const variant = variants.get(upsell.bundleLineVariantId);
    if (!variant) {
      Log.warn(
        `could not find variant with id: ${upsell.bundleLineVariantId}... Skipping...`,
      );
      continue;
    }
    result.push({
      id: v7(),
      type: "simple",
      productId: variant.productId,
      quantity: upsell.quantity,
    });
  }
  return result;
}

type BookingEmitted =
  | { type: "creatingBooking" }
  | { type: "createdBooking" }
  | { type: "validatingBookingIsPaidFor" }
  | { type: "completedBooking"; data: { bookingId: string } }
  | { type: "creatingPayment" }
  | { type: "createdPayment" }
  | { type: "paying" }
  | {
      type: "practicalInfoChanged";
      data: { change: PracticalInfoChange; state: PracticalInfoForm };
    }
  | { type: "cartModalOpened" }
  | { type: "cartModalClosed" }
  | { type: "paymentMethodPickerModalOpened" }
  | { type: "paymentMethodPickerModalClosed" }
  | { type: "bundlePickerModalOpened" }
  | { type: "bundlePickerModalClosed" }
  | { type: "failedToFetchTicketEvents"; error: Error }
  | { type: "failedToCreateBooking"; error: Error }
  | { type: "failedToCreatePayment"; error: Error }
  | { type: "failedToFetchBundles"; error: Error }
  | { type: "failedToFetchActivePeriods"; error: Error }
  | { type: "failedToFetchSpecialPricePeriods"; error: Error }
  | { type: "failedToFetchVenue"; error: Error }
  | { type: "failedToFetchFloorPlan"; error: Error }
  | { type: "failedToFetchBookedTables"; error: Error }
  | { type: "failedToFetchLockedTables"; error: Error }
  | { type: "failedToFetchBooking"; error: Error }
  | { type: "failedToFetchBookings"; error: Error }
  | { type: "failedToFetchBookingSettings"; error: Error }
  | { type: "failedToFetchGreeterEvents"; error: Error }
  | { type: "failedToFetchSpecialOpeningHours"; error: Error }
  | { type: "failedToValidateBookingWasPaidFor"; error: Error }
  | { type: "failedPayment" };

export const BaseBookingContextSchema = z.object({
  venueId: z.string(),
  venue: VenueSchema.optional(),
  bookedTables: z.array(TableIdSchema),
  lockedTables: z.array(TableIdSchema),
  floorPlan: FloorPlanSchema.optional(),
  specialOpeningHours: z.array(SpecialOpeningHoursSchema),
  bookingId: z.string().optional(),
  booking: BookingSchema.optional(),
  bookingsForArrivalDate: z.array(BookingSchema).default([]),
  stripePaymentToken: z.string().optional(),
  cart: CartMapSchema,
  bundles: z.array(BundleSchema),
  bookingSettings: BookingSettingsSchema.optional(),
  greeterEvents: z.array(GreeterEventSchema),

  ticketEvents: z.array(TicketEventSchema),
  ticketEventsForArrivalTime: z.array(TicketEventSchema).optional(),
  ticketEventBundles: z.array(BundleSchema).default([]),

  backgroundFetchMode: z.union([z.literal("fetching"), z.literal("paused")]),

  specialPricePeriods: SpecialPricePeriodsSchema,
  activePeriods: ActivePeriodsSchema,

  practicalInfoChanges: ChangeStateSchema(
    PracticalInfoFormSchema,
    PracticalInfoChangeSchema,
  ),
});
export type BookingContext = z.infer<typeof BaseBookingContextSchema> & {
  // NOTE: There is no schema type for ZodErrorTree sadly and so we have to do this
  practicalInfoValidationErrors?: $ZodErrorTree<PracticalInfoForm>;
};
export const BookingContextSchema: z.ZodType<BookingContext> =
  BaseBookingContextSchema.extend({
    practicalInfoValidationErrors: z.optional(z.any()),
  });

export const BookingContextPersistedSnapshotSchema =
  PersistedSnapshotSchema(BookingContextSchema);
export type BookingContextPersistedSnapshot = z.infer<
  typeof BookingContextPersistedSnapshotSchema
>;

type BookingEvents =
  | { type: "changePracticalInfo"; data: PracticalInfoChange }
  | { type: "openBundlePickerModal" }
  | { type: "setCart"; data: CartMap }
  | { type: "openLoginModal" }
  | { type: "openPaymentModal" }
  | { type: "openUpdateCustomerInfoModal" }
  | { type: "fetchedBundles" }
  | { type: "fetchBookingsForArrivalDate" }
  | { type: "createPayment" }
  | { type: "createBooking" }
  | { type: "closeModal" }
  | { type: "openCartModal" }
  | { type: "setBookingId"; data: string }
  | { type: "setBooking"; data: Booking | undefined }
  | { type: "ready" }
  | { type: "next" }
  | { type: "prev" }
  | { type: "restart" }
  | { type: "validate" }
  | { type: "pay" }
  | { type: "cancel" }
  | { type: "pauseFetching" }
  | { type: "continueFetching" }
  | {
      type: "setBackgroundFetchMode";
      data: BookingContext["backgroundFetchMode"];
    }
  | { type: "fetchTicketEvents" };

export type BookingMachineApiRequirements = Pick<
  IGuestApi,
  | "fetchBooking"
  | "fetchBookings"
  | "fetchBundlesByVenue"
  | "fetchActivePeriods"
  | "fetchVenue"
  | "fetchProducts"
  | "fetchLockedTables"
  | "fetchLockedTablesByPeriod"
  | "fetchBookedTables"
  | "fetchBookedTablesByPeriod"
  | "fetchFloorPlan"
  | "fetchSpecialOpeningHours"
  | "fetchSpecialPricePeriods"
  | "fetchBookingSettings"
  | "fetchUpcomingGreeterEventsForVenue"
  | "fetchVenueFeatureFlag"
  | "fetchCategories"
  | "fetchTicketEvents"
  | "createBookingV2"
  | "createPayment"
>;

export function bookingMachineWithApi(
  api: BookingMachineApiRequirements,
): ReturnType<typeof bookingMachine.provide> {
  return bookingMachine.provide({
    actors: {
      fetchVenue: adapt(api.fetchVenue.bind(api)),
      fetchBooking: adapt(api.fetchBooking.bind(api)),
      fetchBookings: adapt(api.fetchBookings.bind(api)),
      fetchBundlesByVenue: adapt(api.fetchBundlesByVenue.bind(api)),
      fetchFloorPlan: adapt(api.fetchFloorPlan.bind(api)),
      fetchBookedTables: adapt(api.fetchBookedTables.bind(api)),
      fetchBookedTablesByPeriod: adapt(api.fetchBookedTablesByPeriod.bind(api)),
      fetchLockedTables: adapt(api.fetchLockedTables.bind(api)),
      fetchLockedTablesByPeriod: adapt(api.fetchLockedTablesByPeriod.bind(api)),
      fetchActivePeriods: adapt(api.fetchActivePeriods.bind(api)),
      fetchSpecialPricePeriods: adapt(api.fetchSpecialPricePeriods.bind(api)),
      fetchSpecialOpeningHours: adapt(api.fetchSpecialOpeningHours.bind(api)),
      fetchBookingSettings: adapt(api.fetchBookingSettings.bind(api)),
      fetchUpcomingGreeterEventsForVenue: adapt(
        api.fetchUpcomingGreeterEventsForVenue.bind(api),
      ),
      fetchTicketEvents: adapt(api.fetchTicketEvents.bind(api)),
      createBookingV2: adapt(api.createBookingV2.bind(api)),
      createPayment: adapt(api.createPayment.bind(api)),
    },
  });
}

/**
 * Gets the opening hours opening hours period
 * for the given period.
 *
 * @throws{AssertionError} If missing {@link BookingContext.practicalInfoChanges.value.arrivalDate} or {@link BookingContext.venue.bookableOpeningHours} or it cannot find the period
 *
 */
function getPeriodForArrivalTime(context: BookingContext) {
  const info = context.practicalInfoChanges.value;
  assert(
    info.arrivalDate,
    "cannot fetch booked tables when arrivalDate has not been set",
  );
  assert(
    context.venue?.bookableOpeningHours,
    "context.venue?.bookableOpeningHours is required to fetch booked tables",
  );
  const openingHours = Venue.createDatePeriodsFromOpeningHours({
    specialOpeningHours: context.specialOpeningHours,
    now: subDays(info.arrivalTime, 2),
    range: 10,
    openingHours: context.venue?.bookableOpeningHours,
  });
  const period = openingHours.find((_period) =>
    isWithin(info.arrivalTime!, _period),
  );
  assert(period, "could not find period to fetch booked tables for");
  return period;
}

export const fetchBookedTablesAbortController = new AbortController();
export const fetchLockedTablesAbortController = new AbortController();

export const bookingMachine = setup({
  types: {} as {
    context: BookingContext;
    events: BookingEvents;
    emitted: BookingEmitted;
    input: { venueId: string; bookingId?: string };
  },
  actors: {
    fetchFloorPlan:
      notImplemented<BookingMachineApiRequirements["fetchFloorPlan"]>(),
    fetchBooking:
      notImplemented<BookingMachineApiRequirements["fetchBooking"]>(),
    fetchBookings:
      notImplemented<BookingMachineApiRequirements["fetchBookings"]>(),
    fetchVenue: notImplemented<BookingMachineApiRequirements["fetchVenue"]>(),
    fetchActivePeriods:
      notImplemented<BookingMachineApiRequirements["fetchActivePeriods"]>(),
    fetchSpecialPricePeriods:
      notImplemented<
        BookingMachineApiRequirements["fetchSpecialPricePeriods"]
      >(),
    fetchBundlesByVenue:
      notImplemented<BookingMachineApiRequirements["fetchBundlesByVenue"]>(),
    fetchBookedTables:
      notImplemented<BookingMachineApiRequirements["fetchBookedTables"]>(),
    fetchBookedTablesByPeriod:
      notImplemented<
        BookingMachineApiRequirements["fetchBookedTablesByPeriod"]
      >(),
    fetchLockedTables:
      notImplemented<BookingMachineApiRequirements["fetchLockedTables"]>(),
    fetchLockedTablesByPeriod:
      notImplemented<
        BookingMachineApiRequirements["fetchLockedTablesByPeriod"]
      >(),
    fetchSpecialOpeningHours:
      notImplemented<
        BookingMachineApiRequirements["fetchSpecialOpeningHours"]
      >(),
    fetchBookingSettings:
      notImplemented<BookingMachineApiRequirements["fetchBookingSettings"]>(),
    fetchUpcomingGreeterEventsForVenue:
      notImplemented<
        BookingMachineApiRequirements["fetchUpcomingGreeterEventsForVenue"]
      >(),
    fetchTicketEvents:
      notImplemented<BookingMachineApiRequirements["fetchTicketEvents"]>(),
    createPayment:
      notImplemented<BookingMachineApiRequirements["createPayment"]>(),
    createBookingV2:
      notImplemented<BookingMachineApiRequirements["createBookingV2"]>(),
  },
  guards: {
    hasOpeningHoursPeriod({ context }) {
      const info = context.practicalInfoChanges.value;
      if (!context.venue?.bookableOpeningHours) return false;

      const openingHours = Venue.createDatePeriodsFromOpeningHours({
        specialOpeningHours: context.specialOpeningHours,
        now: subDays(info.arrivalTime, 2),
        range: 10,
        openingHours: context.venue?.bookableOpeningHours,
      });
      const period = openingHours.find((_period) =>
        isWithin(info.arrivalTime!, _period),
      );
      if (!period) return false;
      return (
        !!context.practicalInfoChanges.value.arrivalDate &&
        context.practicalInfoChanges.value.arrivalDate instanceof Date &&
        context.backgroundFetchMode === "fetching"
      );
    },
  },
  actions: {},
}).createMachine({
  type: "parallel",
  context: ({ input }) => {
    return {
      backgroundFetchMode: "fetching",
      venueId: input.venueId,
      bundles: [],
      bookedTables: [],
      lockedTables: [],
      specialOpeningHours: [],
      specialPricePeriods: [],
      activePeriods: [],
      greeterEvents: [],
      bookingsForArrivalDate: [],
      cart: new CartMap(),
      ticketEvents: [],
      ticketEventBundles: [],
      practicalInfoChanges: createChangeState<
        PracticalInfoForm,
        PracticalInfoChange
      >({
        venueId: input.venueId,
        groupSize: 0,
        tables: [],
        // @ts-ignore
        arrivalDate: undefined,
        // @ts-ignore
        arrivalTime: undefined,
        comment: undefined,
        total: 0,
        minPrice: 0,
      }),
      practicalInfoValidationErrors: PracticalInfoFormSchema.safeParse({})
        ?.error
        ? z.treeifyError(PracticalInfoFormSchema.safeParse({}).error!)
        : { errors: [] },
    };
  },
  on: {
    pauseFetching: {
      actions: assign(() => ({ backgroundFetchMode: "paused" })),
    },
    continueFetching: {
      actions: assign(() => ({ backgroundFetchMode: "fetching" })),
    },
    setBackgroundFetchMode: {
      actions: assign(({ event }) => ({ backgroundFetchMode: event.data })),
    },
    setBooking: {
      actions: assign(({ event }) => ({ booking: event.data })),
    },
    setBookingId: {
      actions: [
        assign(({ event }) => {
          return { bookingId: event.data };
        }),
      ],
    },
    setCart: {
      actions: assign(({ event }) => {
        return { cart: event.data };
      }),
    },
    changePracticalInfo: {
      actions: [
        assign(({ event, context }) => {
          const newState = change(
            context.practicalInfoChanges,
            applyPracticalInfoChange,
            event.data,
          );
          const parseResult = PracticalInfoFormSchema.safeParse(newState.value);
          return {
            practicalInfoChanges: { ...newState },
            practicalInfoValidationErrors: parseResult.error
              ? z.treeifyError(parseResult.error)
              : { errors: [] },
          };
        }),
        emit(({ event, context }) => ({
          type: "practicalInfoChanged",
          data: {
            change: event.data,
            state: context.practicalInfoChanges.value,
          },
        })),
        enqueueActions(({ enqueue, event }) => {
          switch (event.data.type) {
            case "setArrivalTime":
            case "setArrivalDate":
              enqueue.raise({ type: "fetchTicketEvents" });
              break;
          }
        }),
      ],
    },
    validate: {
      actions: assign(({ context }) => {
        const parseResult = PracticalInfoFormSchema.safeParse(
          context.practicalInfoChanges.value,
        );
        return {
          practicalInfoValidationErrors: parseResult.error
            ? z.treeifyError(parseResult.error)
            : { errors: [] },
        };
      }),
    },
  },
  states: {
    background: {
      type: "parallel",
      states: {
        specialOpeningHours: {
          initial: "fetching",
          states: {
            idle: {},
            fetching: {
              invoke: {
                src: "fetchSpecialOpeningHours",
                input: ({ context }) => [context.venueId],
                onError: {
                  target: "idle",
                  actions: emit(({ event }) => {
                    return {
                      type: "failedToFetchSpecialOpeningHours",
                      error: event.error as Error,
                    };
                  }),
                },
                onDone: {
                  target: "idle",
                  actions: assign(({ event }) => {
                    return { specialOpeningHours: event.output };
                  }),
                },
              },
            },
          },
        },
        greeterEvents: {
          initial: "fetching",
          states: {
            idle: {},
            fetching: {
              invoke: {
                src: "fetchUpcomingGreeterEventsForVenue",
                input: ({ context }) => [{ venueId: context.venueId }],
                onError: {
                  target: "idle",
                  actions: emit(({ event }) => {
                    return {
                      type: "failedToFetchGreeterEvents",
                      error: event.error as Error,
                    };
                  }),
                },
                onDone: {
                  target: "idle",
                  actions: assign(({ event }) => {
                    return { greeterEvents: event.output };
                  }),
                },
              },
            },
          },
        },
        bookingSettings: {
          initial: "fetching",
          states: {
            idle: {},
            fetching: {
              invoke: {
                src: "fetchBookingSettings",
                input: ({ context }) => [context.venueId],
                onError: {
                  target: "idle",
                  actions: emit(({ event }) => {
                    return {
                      type: "failedToFetchBookingSettings",
                      error: event.error as Error,
                    };
                  }),
                },
                onDone: {
                  target: "idle",
                  actions: assign(({ event }) => {
                    return { bookingSettings: event.output };
                  }),
                },
              },
            },
          },
        },
        bookings: {
          initial: "idle",
          states: {
            idle: {
              on: {
                fetchBookingsForArrivalDate: "fetching",
              },
            },
            fetching: {
              invoke: {
                src: "fetchBookings",
                input({ context }) {
                  const info = context.practicalInfoChanges.value;
                  const venue = context.venue;
                  const openingHours = Venue.createDatePeriodsFromOpeningHours({
                    specialOpeningHours: context.specialOpeningHours,
                    now: subDays(info.arrivalTime, 2),
                    range: 10,
                    openingHours: venue?.bookableOpeningHours!,
                  });
                  const openingHoursForDate = openingHours.find((oh) =>
                    oh.isWithin(info.arrivalTime),
                  );
                  return [openingHoursForDate];
                },
                onDone: {
                  target: "idle",
                  actions: assign(({ event }) => {
                    return { bookingsForArrivalDate: event.output };
                  }),
                },
                onError: {
                  target: "idle",
                  actions: emit(({ event }) => ({
                    type: "failedToFetchBookings",
                    error: event.error as Error,
                  })),
                },
              },
            },
          },
        },
        booking: {
          initial: "idle",
          states: {
            idle: {
              on: {
                createBooking: {
                  target: "creating",
                  actions: [
                    assign(() => {
                      return { stripePaymentToken: undefined };
                    }),
                  ],
                },
              },
            },
            fetching: {
              invoke: {
                src: "fetchBooking",
                input({ context }) {
                  return [context.bookingId ?? ""];
                },
                onDone: {
                  actions: assign(({ context }) => {
                    return { booking: context.booking };
                  }),
                },
                onError: {
                  target: "idle",
                  actions: emit(({ event }) => ({
                    type: "failedToFetchBooking",
                    error: event.error as Error,
                  })),
                },
              },
            },
            creatingFailed: { on: { createBooking: "creating" } },
            creating: {
              on: {
                cancel: "idle",
              },
              invoke: {
                src: "createBookingV2",
                input({ context }) {
                  const info = context.practicalInfoChanges.value;
                  const cart = context.cart;
                  let orderLines: Array<OrderLineRequest> = [];

                  for (const [_k, v] of cart.entries()) {
                    switch (v.type) {
                      case "bundle":
                        orderLines.push({
                          type: "bundle",
                          id: v.id,
                          bundleId: v.bundleId,
                          picks: mapCartItemToRecord(v),
                          quantity: v.quantity,
                        });
                        break;

                      case "bundle-form":
                        orderLines.push({
                          type: "bundle",
                          id: v.id,
                          bundleId: v.form.id,
                          picks: mapBundleFormToPicks(v),
                          quantity: v.quantity,
                        });
                        mapBundleFormExtrasToSimpleOrderLineRequests(v).forEach(
                          pushTo(orderLines),
                        );

                        // Merge duplicates
                        const merged = new Set<OrderLineRequest>();
                        for (let i = 0; i < orderLines.length; i++) {
                          const aOrderLine = orderLines[i];
                          if (!isType(aOrderLine, "simple")) continue;
                          if (merged.has(aOrderLine)) continue;

                          for (let j = i + 1; j < orderLines.length; j++) {
                            const bOrderLine = orderLines[j];
                            const isSelf = aOrderLine === bOrderLine;

                            if (!isType(bOrderLine, "simple")) continue;
                            if (isSelf) continue;
                            if (merged.has(bOrderLine)) continue;

                            const isDuplicate =
                              aOrderLine.productId === bOrderLine.productId;

                            if (isDuplicate) {
                              aOrderLine.quantity += bOrderLine.quantity;
                              merged.add(bOrderLine);
                            }
                          }
                        }

                        Log.debug(
                          "[CreateBookingMachine][booking.creating] After merging, before filtering",
                          {
                            merged,
                            orderLines,
                          },
                        );

                        // Remove the duplicates
                        orderLines = orderLines.filter((ol) => !merged.has(ol));

                        Log.debug(
                          "[CreateBookingMachine][booking.creating] After merging, after filtering",
                          {
                            merged,
                            orderLines,
                          },
                        );

                        break;

                      case "simple":
                        throw new NotImplementedError();
                    }
                  }

                  console.log("[createBookingV2]", { orderLines });
                  assert(
                    orderLines.length,
                    "orderlines.length is 0. Should be >0",
                  );
                  assert(info.tables, "tables cannot be falsy");
                  assert(info.groupSize, "groupSize cannot be falsy");
                  assert(info.arrivalTime, "arrivalTime cannot be falsy");
                  assert(context.venue, "venue cannot be undefined");

                  const openingHours = Venue.createDatePeriodsFromOpeningHours({
                    specialOpeningHours: context.specialOpeningHours,
                    now: subDays(info.arrivalTime, 2),
                    range: 10,
                    openingHours: context.venue?.bookableOpeningHours,
                  });
                  const period = openingHours.find((_period) =>
                    isWithin(info.arrivalTime!, _period),
                  );
                  assert(period, "cannot find opening hours for date");

                  const request: CreateBookingV2Request = {
                    id:
                      context.booking?.status === "created"
                        ? context.booking?.id
                        : undefined,
                    tables: info.tables,
                    venueId: context.venueId,
                    groupSize: info.groupSize,
                    lines: orderLines,
                    comment: info.comment ?? "",
                    period: new DatePeriod({
                      from: info.arrivalTime,
                      to: period.to,
                    }),
                  };

                  console.debug("[OrderLines] the request!", request);
                  return [request];
                },
                onDone: {
                  target: "idle",
                  actions: [
                    assign(({ event }) => {
                      return {
                        booking: event.output,
                        bookingId: event.output.id,
                      };
                    }),
                    assign(({ context }) => {
                      return {
                        practicalInfoChanges: createChangeState(
                          context.practicalInfoChanges.value,
                        ),
                      };
                    }),
                    emit({ type: "createdBooking" }),
                  ],
                },
                onError: {
                  target: "creatingFailed",
                  actions: [
                    emit(({ event }) => ({
                      type: "failedToCreateBooking",
                      error: event.error as ApiError,
                    })),
                    console.error,
                  ],
                },
              },
            },
          },
        },
        ticketEvents: {
          initial: "idle",
          states: {
            idle: {
              on: {
                fetchTicketEvents: "fetching",
              },
            },
            fetching: {
              invoke: {
                src: "fetchTicketEvents",
                input({ context, event }) {
                  console.log("[fetchTicketEvents.fetching]", { event });
                  const start = startOfMonth(
                    context.practicalInfoChanges.value.arrivalDate,
                  );
                  const end = endOfMonth(
                    context.practicalInfoChanges.value.arrivalDate,
                  );
                  return [
                    {
                      venueId: context.venueId,
                      period: new DatePeriod({ from: start, to: end }),
                    },
                  ];
                },
                onError: {
                  target: "idle",
                  actions: [
                    ({ event }) =>
                      Log.error(
                        "[CreateBookingMachineV2.fetchTicketEvents.error]",
                        event.error,
                      ),
                  ],
                },
                onDone: {
                  target: "idle",
                  actions: [
                    assign(({ context, event }) => {
                      const ticketEventsForArrivalTime = event.output.filter(
                        (te: TicketEvent) => {
                          if (te.eventPeriod.type === "once") {
                            const datePeriod = DatePeriod.fromTimePeriod(
                              new Date(te.eventPeriod.date),
                              te.eventPeriod.timePeriod.from,
                              te.eventPeriod.timePeriod.to,
                            );

                            if (
                              context.practicalInfoChanges.value.arrivalTime
                            ) {
                              return datePeriod.isWithin(
                                context.practicalInfoChanges.value.arrivalTime,
                              );
                            }

                            return datePeriod.isWithin(
                              context.practicalInfoChanges.value.arrivalDate,
                            );
                          }
                        },
                      );

                      const bundleIds = new Set(
                        ticketEventsForArrivalTime.map((t) => t.bundles).flat(),
                      );
                      const ticketEventBundles = context.bundles.filter((b) =>
                        bundleIds.has(b.id),
                      );

                      return {
                        ticketEvents: event.output,
                        ticketEventsForArrivalTime,
                        ticketEventBundles,
                      };
                    }),
                  ],
                },
              },
            },
          },
        },
        payment: {
          initial: "idle",
          states: {
            idle: {
              on: {
                createPayment: "creating",
              },
            },
            creatingFailed: {
              on: {
                createPayment: "creating",
              },
            },
            creating: {
              entry: emit({ type: "creatingPayment" }),
              invoke: {
                src: "createPayment",
                input({ context }) {
                  assert(
                    context.booking,
                    "cannot create payment without a booking",
                  );
                  assert(
                    context.booking.order,
                    "cannot create payment without a booking.order",
                  );

                  return [
                    {
                      orderId: context.booking.order.id,
                      redirectUrl:
                        window.location.origin +
                        Routes.bookingSummary.route({
                          bookingId: context.booking.id,
                        }),
                    },
                  ];
                },
                onDone: {
                  target: "idle",
                  actions: [
                    assign(({ event }) => {
                      return { stripePaymentToken: event.output.token };
                    }),
                    emit({ type: "createdPayment" }),
                  ],
                },
                onError: {
                  target: "creatingFailed",
                  actions: [
                    emit(({ event }) => ({
                      type: "failedToCreatePayment",
                      error: event.error as Error,
                    })),
                  ],
                },
              },
            },
          },
        },
        bundles: {
          initial: "fetching",
          states: {
            idle: {},
            fetching: {
              invoke: {
                src: "fetchBundlesByVenue",
                input: ({ context }) => [context.venueId],
                onError: {
                  target: "idle",
                  actions: emit(({ event }) => {
                    return {
                      type: "failedToFetchBundles",
                      error: event.error as Error,
                    };
                  }),
                },
                onDone: {
                  target: "idle",
                  actions: [
                    assign(({ event }) => {
                      return { bundles: event.output };
                    }),
                    raise({ type: "fetchedBundles" }),
                  ],
                },
              },
            },
          },
        },
        specialPricePeriods: {
          initial: "idle",
          states: {
            idle: {
              on: {
                fetchedBundles: "fetching",
              },
            },
            fetching: {
              invoke: {
                src: "fetchSpecialPricePeriods",
                input({ context }) {
                  return [context.bundles.map((b) => b.id)];
                },
                onError: {
                  target: "idle",
                  actions: emit(({ event }) => {
                    return {
                      type: "failedToFetchSpecialPricePeriods",
                      error: event.error as Error,
                    };
                  }),
                },
                onDone: {
                  target: "idle",
                  actions: assign(({ event }) => {
                    return { specialPricePeriods: event.output };
                  }),
                },
              },
            },
          },
        },
        activePeriods: {
          initial: "idle",
          states: {
            idle: {
              on: {
                fetchedBundles: "fetching",
              },
            },
            fetching: {
              invoke: {
                src: "fetchActivePeriods",
                input: ({ context }) => [
                  {
                    productIds: context.bundles.map((b) => b.id),
                  },
                ],
                onError: {
                  target: "idle",
                  actions: emit(({ event }) => {
                    return {
                      type: "failedToFetchActivePeriods",
                      error: event.error as Error,
                    };
                  }),
                },
                onDone: {
                  target: "idle",
                  actions: assign(({ event }) => {
                    return { activePeriods: event.output };
                  }),
                },
              },
            },
          },
        },
        venue: {
          initial: "fetching",
          states: {
            idle: {},
            fetching: {
              invoke: {
                src: "fetchVenue",
                input: ({ context }) => [context.venueId],
                onDone: {
                  target: "idle",
                  actions: [
                    assign(({ event }) => ({ venue: event.output })),
                    raise({ type: "ready" }),
                  ],
                },
                onError: {
                  target: "idle",
                  actions: emit(({ event }) => {
                    return {
                      type: "failedToFetchVenue",
                      error: event.error as Error,
                    };
                  }),
                },
              },
            },
          },
        },
        floorPlan: {
          initial: "fetching",
          states: {
            idle: {},
            fetching: {
              invoke: {
                src: "fetchFloorPlan",
                input: ({ context }) => [context.venueId],
                onError: {
                  target: "idle",
                  actions: emit(({ event }) => {
                    return {
                      type: "failedToFetchFloorPlan",
                      error: event.error as Error,
                    };
                  }),
                },
                onDone: {
                  target: "idle",
                  actions: assign(({ event }) => ({ floorPlan: event.output })),
                },
              },
            },
          },
        },
        bookedTables: {
          initial: "idle",
          states: {
            idle: {
              on: {
                restart: "decidingToFetch",
              },
              after: {
                1_000: {
                  target: "decidingToFetch",
                },
              },
            },
            decidingToFetch: {
              after: {
                0: [
                  {
                    target: "fetching",
                    guard: "hasOpeningHoursPeriod",
                  },
                  { target: "idle" },
                ],
              },
            },
            fetching: {
              invoke: {
                src: "fetchBookedTablesByPeriod",
                input({ context }) {
                  const arrivalTime =
                    context.practicalInfoChanges.value.arrivalTime!;

                  const period = getServiceDayPeriod({
                    bookingArrivalTime: arrivalTime,
                  });
                  return [
                    { venueId: context.venueId, period: period.toStrict() },
                  ];
                },
                onError: {
                  target: "idle",
                  actions: emit(({ event }) => {
                    return {
                      type: "failedToFetchBookedTables",
                      error: event.error as Error,
                    };
                  }),
                },
                onDone: {
                  target: "idle",
                  actions: assign(({ event }) => ({
                    bookedTables: event.output,
                  })),
                },
              },
            },
          },
        },
        lockedTables: {
          initial: "idle",
          states: {
            idle: {
              after: {
                1_000: [
                  {
                    target: "decidingToFetch",
                  },
                ],
              },
              on: {
                restart: "decidingToFetch",
              },
            },
            decidingToFetch: {
              after: {
                0: [
                  {
                    target: "fetching",
                    guard: "hasOpeningHoursPeriod",
                  },
                  { target: "idle" },
                ],
              },
            },
            fetching: {
              invoke: {
                src: "fetchLockedTablesByPeriod",
                input: ({ context }) => {
                  const arrivalTime =
                    context.practicalInfoChanges.value.arrivalTime!;
                  const period = getServiceDayPeriod({
                    bookingArrivalTime: arrivalTime,
                  });
                  return [
                    { venueId: context.venueId, period: period?.toStrict() },
                  ];
                },
                onError: {
                  target: "idle",
                  actions: emit(({ event }) => {
                    return {
                      type: "failedToFetchLockedTables",
                      error: event.error as Error,
                    };
                  }),
                },
                onDone: {
                  target: "idle",
                  actions: assign(({ event }) => ({
                    lockedTables: event.output.map((lt) => lt.table),
                  })),
                },
              },
            },
          },
        },
      },
    },
    modal: {
      initial: "closed",
      on: {
        closeModal: ".closed",
        openCartModal: ".cart",
        openPaymentModal: ".paymentMethodPicker",
        openBundlePickerModal: ".bundlePicker",
        openUpdateCustomerInfoModal: ".updateCustomerInfo",
        openLoginModal: ".login",
      },
      states: {
        closed: {},
        login: {},
        updateCustomerInfo: {},
        cart: {
          entry: emit({ type: "cartModalOpened" }),
          exit: emit({ type: "cartModalClosed" }),
        },
        paymentMethodPicker: {
          entry: emit({ type: "paymentMethodPickerModalOpened" }),
          exit: emit({ type: "paymentMethodPickerModalClosed" }),
          on: {
            closeModal: {
              target: "closed",
            },
            cancel: {
              target: "closed",
              actions: assign(() => {
                return { stripePaymentToken: undefined };
              }),
            },
          },
        },
        bundlePicker: {
          entry: emit({ type: "bundlePickerModalOpened" }),
          exit: emit({ type: "bundlePickerModalClosed" }),
        },
      },
    },
    ui: {
      initial: "initializing",
      states: {
        initializing: {
          on: {
            ready: {
              target: "practicalInfo",
              guard({ context }) {
                return !!context.venue;
              },
            },
          },
        },

        practicalInfo: {
          on: { next: "paying" },
        },

        paying: {
          entry: emit({ type: "paying" }),
          on: {
            next: {
              target: "completedBooking",
            },
            cancel: {
              target: "practicalInfo",
              actions: emit({ type: "failedPayment" }),
            },
          },
        },

        completedBooking: {
          entry: emit(({ context }) => ({
            type: "completedBooking",
            data: { bookingId: context.booking!.id },
          })),
        },
      },
    },
  },
});

export type BookingMachine = ActorRefFrom<typeof bookingMachine>;
